/**
 * Circle to ask, the drawing half: ink that behaves like a felt-tip pen. Pointer samples are smoothed a little (a pen
 * trails the hand and the jitter goes), and the stroke is drawn as an outline whose width follows the hand: heavier
 * where it slows, lighter where it sweeps, a stylus's own pressure when there is one, and a softer start and end.
 */
import { loopAround, type Box, type Pt } from './region';

export interface InkPt extends Pt {
  /** Pressure, 0 to 1. */
  p: number;
  /** When the sample was taken, in milliseconds. */
  t: number;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
export const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);
const mid = (a: Pt, b: Pt) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
const f = (p: Pt) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`;

/** Add a pointer sample to the stroke. `pressure` is a stylus's; a mouse's or a finger's is worked out from its speed. */
export function addInk(pts: InkPt[], x: number, y: number, t: number, pressure?: number): void {
  const last = pts[pts.length - 1];
  if (!last) {
    pts.push({ x, y, t, p: pressure ?? 0.7 });
    return;
  }
  const next = { x: last.x + (x - last.x) * 0.6, y: last.y + (y - last.y) * 0.6 };
  const d = dist(next, last);
  if (d < 0.75) return;
  const dt = Math.max(t - last.t, 1);
  const target = pressure ?? clamp(1.15 - (d / dt) * 0.4, 0.25, 1);
  pts.push({ ...next, t, p: last.p + (target - last.p) * (1 - Math.exp(-dt / 40)) });
}

/** The pen's width at each point, in pixels. A finished stroke tapers where the pen lifted, too. */
function widths(pts: InkPt[], done: boolean): number[] {
  const along = [0];
  for (let i = 1; i < pts.length; i++) along.push(along[i - 1]! + dist(pts[i]!, pts[i - 1]!));
  const total = along[along.length - 1]!;
  return pts.map((q, i) => {
    let w = 1.7 + 3.7 * q.p;
    w *= 0.55 + 0.45 * Math.min(1, along[i]! / 10);
    if (done) w *= 0.5 + 0.5 * Math.min(1, (total - along[i]!) / 14);
    return w;
  });
}

/** The stroke as a filled SVG outline. */
export function inkPath(pts: InkPt[], done = false): string {
  if (!pts.length) return '';
  const n = pts.length;
  const ws = widths(pts, done);
  if (n === 1) {
    const r = ws[0]! / 2;
    const p = pts[0]!;
    return `M${(p.x - r).toFixed(1)},${p.y.toFixed(1)}a${r.toFixed(2)},${r.toFixed(2)} 0 1 0 ${(2 * r).toFixed(2)},0a${r.toFixed(2)},${r.toFixed(2)} 0 1 0 ${(-2 * r).toFixed(2)},0Z`;
  }
  const left: Pt[] = [];
  const right: Pt[] = [];
  for (let i = 0; i < n; i++) {
    // The direction over a few points either side, so a small wobble doesn't kink the edges.
    const a = pts[Math.max(0, i - 2)]!;
    const b = pts[Math.min(n - 1, i + 2)]!;
    const len = dist(a, b) || 1;
    const nx = -(b.y - a.y) / len;
    const ny = (b.x - a.x) / len;
    const h = ws[i]! / 2;
    const p = pts[i]!;
    left.push({ x: p.x + nx * h, y: p.y + ny * h });
    right.push({ x: p.x - nx * h, y: p.y - ny * h });
  }
  right.reverse();
  const r1 = (ws[n - 1]! / 2).toFixed(2);
  const r0 = (ws[0]! / 2).toFixed(2);
  return `M${f(left[0]!)}${through(left)}A${r1},${r1} 0 0 0 ${f(right[0]!)}${through(right)}A${r0},${r0} 0 0 0 ${f(left[0]!)}Z`;
}

/** Smooth curves through the points, from the first (already drawn to) to the last. */
function through(ps: Pt[]): string {
  let d = '';
  for (let i = 1; i < ps.length - 1; i++) d += `Q${f(ps[i]!)} ${f(mid(ps[i]!, ps[i + 1]!))}`;
  return `${d}L${f(ps[ps.length - 1]!)}`;
}

/** How far round a path turns, in radians, signed: about 2π for one loop, near 0 for a line or a zigzag. */
export function turning(pts: Pt[]): number {
  let sum = 0;
  let prev: number | null = null;
  for (let i = 1; i < pts.length; i++) {
    const a = Math.atan2(pts[i]!.y - pts[i - 1]!.y, pts[i]!.x - pts[i - 1]!.x);
    if (prev !== null) {
      let d = a - prev;
      if (d > Math.PI) d -= 2 * Math.PI;
      if (d < -Math.PI) d += 2 * Math.PI;
      sum += d;
    }
    prev = a;
  }
  return sum;
}

/**
 * The rest of a loop that stopped short: the pen carried round from where it lifted to where it began, bowing outward
 * as a hand's does, and a little past the start. Nothing when the ends already meet.
 */
export function closeLoop(pts: InkPt[]): InkPt[] {
  const a = pts[pts.length - 1]!;
  const b = pts[0]!;
  const gap = dist(a, b);
  if (gap < 6) return [];
  const c = pts.reduce((s, p) => ({ x: s.x + p.x / pts.length, y: s.y + p.y / pts.length }), { x: 0, y: 0 });
  const m = mid(a, b);
  const out = dist(m, c) || 1;
  const ctl = { x: m.x + ((m.x - c.x) / out) * gap * 0.33, y: m.y + ((m.y - c.y) / out) * gap * 0.33 };
  const s = pts[Math.min(pts.length - 1, 6)]!;
  const sl = dist(s, b) || 1;
  const end = { x: b.x + ((s.x - b.x) / sl) * 8, y: b.y + ((s.y - b.y) / sl) * 8 };
  const steps = Math.max(4, Math.ceil(gap / 3));
  const tail: InkPt[] = [];
  for (let i = 1; i <= steps; i++) {
    const k = i / steps;
    tail.push({
      x: (1 - k) ** 2 * a.x + 2 * (1 - k) * k * ctl.x + k ** 2 * end.x,
      y: (1 - k) ** 2 * a.y + 2 * (1 - k) * k * ctl.y + k ** 2 * end.y,
      t: a.t + i * 8,
      p: a.p + (0.45 - a.p) * k,
    });
  }
  return tail;
}

/** A loop the app draws around a block, as a hand would: pressing harder through the middle of the stroke. */
export function handLoop(r: Box): InkPt[] {
  const pts = loopAround(r, 96);
  return pts.map((p, i) => ({ ...p, t: i * 6, p: 0.4 + 0.45 * Math.sin((Math.PI * i) / (pts.length - 1)) }));
}
